import * as React from "react";
import { useMutation, useQuery } from "react-query";
import {
  ApiKeyCreateRequest,
  ApiKeyCreateResponse,
  ApiKeysService,
  ApiKeyQuotaPeriod,
  ClientsService
} from "gen/clients";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { DrawerHeader } from "components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "components/DrawerBody/DrawerBody";
import Drawer from "@mui/material/Drawer";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { Button, Stack, Alert, Box, Typography } from "@mui/material";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { SwitchField } from "components/formikFields/SwitchField/SwitchField";
import { CopyToClipboardButton } from "components/CopyToClipboardButton/CopyToClipboardButton";
import { SELECT_OPTION_COMPARATOR } from "utils/stringUtils";

interface Props {
  clientId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

enum FieldName {
  name = "name",
  description = "description",
  clientContactId = "clientContactId",
  rateLimit = "rateLimit",
  quotaLimit = "quotaLimit",
  quotaPeriod = "quotaPeriod",
  mtlsRequired = "mtlsRequired"
}

interface FormValues {
  [FieldName.name]: string;
  [FieldName.description]: string;
  [FieldName.clientContactId]: string;
  [FieldName.rateLimit]: string;
  [FieldName.quotaLimit]: string;
  [FieldName.quotaPeriod]: string;
  [FieldName.mtlsRequired]: boolean;
}

const validationSchema = Yup.object().shape({
  [FieldName.name]: Yup.string().required("Name is required"),
  [FieldName.description]: Yup.string(),
  [FieldName.clientContactId]: Yup.string().required("Client Contact is required"),
  [FieldName.rateLimit]: Yup.number()
    .transform(value => (isNaN(value) ? undefined : value))
    .positive("Rate limit must be a positive number")
    .nullable(),
  [FieldName.quotaLimit]: Yup.number()
    .transform(value => (isNaN(value) ? undefined : value))
    .positive("Quota limit must be a positive number")
    .nullable(),
  [FieldName.quotaPeriod]: Yup.string().when(FieldName.quotaLimit, {
    is: (quotaLimit: string) => quotaLimit && quotaLimit.toString().trim() !== "",
    then: schema => schema.required("Quota period is required when quota limit is set"),
    otherwise: schema => schema.nullable()
  })
});

const CreateApiKeyPanel: React.FC<Props> = ({ clientId, open, onClose, onSuccess }) => {
  const [createdApiKey, setCreatedApiKey] = React.useState<ApiKeyCreateResponse | null>(null);

  const { mutateAsync, error, isLoading } = useMutation(ApiKeysService.createApiKey, {
    onSuccess: data => {
      setCreatedApiKey(data);
    }
  });

  const {
    data: clientContacts,
    isLoading: isLoadingContacts,
    isError: isContactsError
  } = useQuery(["listClientContacts", clientId], {
    queryFn: () => ClientsService.listClientContacts({ clientId }),
    enabled: open,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const clientContactOptions = React.useMemo(() => {
    if (!clientContacts?.items) {
      return [];
    }
    return clientContacts.items
      .map(contact => ({
        label: `${contact.firstName} ${contact.lastName} (${contact.email})`,
        value: contact.id.toString()
      }))
      .sort(SELECT_OPTION_COMPARATOR);
  }, [clientContacts?.items]);

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      if (!clientContacts) {
        return;
      }
      const clientContact = clientContacts.items.find(cc => cc.id === Number(values.clientContactId));
      if (!clientContact) {
        return;
      }
      const request: ApiKeyCreateRequest = {
        name: values.name,
        clientId,
        clientContact: {
          id: clientContact.id,
          email: clientContact.email,
          firstName: clientContact.firstName,
          lastName: clientContact.lastName
        },
        description: values.description || undefined,
        rateLimit: values.rateLimit ? Number(values.rateLimit) : undefined,
        quotaLimit: values.quotaLimit ? Number(values.quotaLimit) : undefined,
        quotaPeriod: values.quotaPeriod ? (values.quotaPeriod as ApiKeyQuotaPeriod) : undefined,
        mtls: values.mtlsRequired ? { required: true } : undefined
      };

      await mutateAsync({ requestBody: request });
    },
    [clientContacts, clientId, mutateAsync]
  );

  const handleClose = React.useCallback(() => {
    if (createdApiKey) {
      onSuccess();
    } else {
      onClose();
    }
    setCreatedApiKey(null);
  }, [createdApiKey, onClose, onSuccess]);

  return (
    <Drawer open={open} onClose={handleClose} anchor="right" PaperProps={{ sx: { width: 600 } }}>
      <DrawerHeader title="Add API Key" onClose={handleClose} />
      <DrawerBody>
        {createdApiKey ? (
          <Stack spacing={3}>
            <Alert severity="success">API key created successfully</Alert>
            <Typography variant="h6">API Key Value</Typography>
            <Typography variant="body2" color="text.secondary">
              This is the only time the API key value will be displayed. Please copy it now.
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.100",
                borderRadius: 1,
                fontFamily: "monospace",
                wordBreak: "break-all"
              }}
            >
              {createdApiKey.value}
            </Box>
            <Stack direction="row" spacing={1}>
              <CopyToClipboardButton
                textToCopy={createdApiKey.value}
                variant="contained"
                color="primary"
                successMessage="API key copied to clipboard"
              >
                Copy API Key
              </CopyToClipboardButton>
              <Button variant="outlined" onClick={handleClose}>
                Close
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Formik
            initialValues={{
              [FieldName.name]: "",
              [FieldName.description]: "",
              [FieldName.clientContactId]: "",
              [FieldName.rateLimit]: "",
              [FieldName.quotaLimit]: "",
              [FieldName.quotaPeriod]: "",
              [FieldName.mtlsRequired]: false
            }}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
          >
            {({ isSubmitting }) => (
              <Form noValidate={true}>
                <Stack spacing={3}>
                  <TextInputField
                    name={FieldName.name}
                    label="Key Name"
                    required={true}
                    helperText="A descriptive name for this API key"
                  />
                  <TextInputField
                    name={FieldName.description}
                    label="Description"
                    helperText="Optional description of the key's purpose"
                  />
                  <SelectOneField
                    name={FieldName.clientContactId}
                    label="Client Contact"
                    required={true}
                    helperText="Select the client contact to associate with this API key"
                    isLoading={isLoadingContacts}
                    isError={isContactsError}
                    options={clientContactOptions}
                  />
                  <TextInputField
                    name={FieldName.rateLimit}
                    label="Rate Limit"
                    type="number"
                    helperText="Maximum requests per second (optional)"
                  />
                  <TextInputField
                    name={FieldName.quotaLimit}
                    label="Quota Limit"
                    type="number"
                    helperText="Maximum requests per quota period (optional)"
                  />
                  <SelectOneField
                    name={FieldName.quotaPeriod}
                    label="Quota Period"
                    helperText="Period for the quota limit (required when quota limit is set)"
                    options={[
                      { label: "Day", value: ApiKeyQuotaPeriod.DAY },
                      { label: "Week", value: ApiKeyQuotaPeriod.WEEK },
                      { label: "Month", value: ApiKeyQuotaPeriod.MONTH }
                    ]}
                  />
                  <SwitchField
                    name={FieldName.mtlsRequired}
                    label="Require mTLS"
                    helperText="Require callers to present a valid client certificate when using this API key"
                  />
                </Stack>

                <Stack spacing={1} marginTop={4}>
                  {!!error && <ApiErrorMessage apiError={error} />}
                  <Stack spacing={1} direction="row">
                    <LoadingButton isLoading={isSubmitting || isLoading}>Create API Key</LoadingButton>
                    <Button color="secondary" onClick={onClose} disabled={isSubmitting || isLoading}>
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        )}
      </DrawerBody>
    </Drawer>
  );
};

export { CreateApiKeyPanel };
